import { Component, ElementRef, Inject, LOCALE_ID, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgRedux } from '@angular-redux/store';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { BaseComponent, LogService } from '@ridango/core';
import { TranslateService } from '@ngx-translate/core';
import { FeedbackCategories, FeedbackHistory, FeedbackService } from '@ridango/components/lib/feedback';
import { CustomerService, NotificationFacade, SessionService } from '@ridango/components';
import { take, takeUntil } from 'rxjs/operators';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { environment } from '../../../../src/environments/environment';

enum FeedbackTab {
    Form = 0,
    History = 1
}

@Component({
    selector: 'gl-feedback-page',
    templateUrl: './feedback-page.component.html',
    styleUrls: ['./feedback-page.component.scss']
})
export class FeedbackPageComponent extends BaseComponent implements OnInit, OnDestroy {
    @ViewChild('fileInput') fileInput: ElementRef;

    readonly maxLength = 500;

    feedbackForm: FormGroup;
    feedbackCategories: FeedbackCategories[] = null;
    selectedFile: File = null;
    terms;
    termsUrl: string;
    termsAgreed = false;
    feedbackHistoryList: FeedbackHistory[];
    customer;
    showSubmitBtn = true;
    tokenVisible = false;
    siteKey: string = environment.recaptcha.siteKey;
    recaptchaValue = '';
    constructor(
        @Inject(LOCALE_ID) public locale: string,
        private ls: LogService,
        private notification: NotificationFacade,
        private translate: TranslateService,
        private feedbackService: FeedbackService,
        private ngRedux: NgRedux<any>,
        private customerService: CustomerService,
        private session: SessionService
    ) {
        super();
    }

    ngOnInit() {
        this.terms = this.ngRedux.getState().core.region.terms;

        if (this.terms) {
            this.termsUrl = `${this.terms.content_base_url}/${this.translate.currentLang}/`;
        }

        this.feedbackService
            .getFeedbackCategories(this.translate.currentLang)
            .pipe(take(1))
            .subscribe((res) => {
                this.feedbackCategories = res;
            });

        this.feedbackForm = new FormGroup({
            name: new FormControl('', [Validators.required]),
            email: new FormControl('', [Validators.required, Validators.email]),
            category: new FormControl('', [Validators.required]),
            subject: new FormControl(''),
            message: new FormControl('', [Validators.required, Validators.maxLength(this.maxLength)])
        });

        if (this.session.getLoginStatus()) {
            this.customerService
                .fetchCustomerProfile()
                .pipe(take(1))
                .subscribe((res) => {
                    this.customer = res;
                    this.prefillForm();
                    this.loadFeedbackHistory();
                });
        }
    }

    private prefillForm(): void {
        if (this.customer?.name) {
            this.feedbackForm.controls['name'].patchValue(this.customer.name);
        }
        if (this.customer?.email) {
            this.feedbackForm.controls['email'].patchValue(this.customer.email);
        }
    }

    private resetForm(): void {
        this.feedbackForm.reset();
        this.feedbackForm.markAsPristine();
        this.termsAgreed = false;
        this.selectedFile = null;
    }

    private loadFeedbackHistory(): void {
        this.feedbackService
            .getFeedbackHistory(this.translate.currentLang)
            .pipe(take(1))
            .subscribe((fb) => (this.feedbackHistoryList = fb));
    }

    submitFeedback(): void {
        if (this.feedbackForm.valid && this.termsAgreed) {
            this.feedbackForm.controls.subject.patchValue(this.feedbackForm.controls.category.value.translation);
            this.feedbackService
                .saveFeedback(this.feedbackForm.value, this.translate.currentLang, this.selectedFile)
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe(
                    () => {
                        this.resetForm();
                        this.prefillForm();
                        this.loadFeedbackHistory();
                        this.notification.success(this.translate.instant('FEEDBACK_FORM_SAVED_SUCCESS'));
                    },
                    (e) => {
                        this.ls.log(this, 'submitFeedback', e);
                        this.notification.error(this.translate.instant('FEEDBACK_FORM_SAVE_ERROR'));
                    }
                );
        }
    }

    onFileSelected(e): void {
        this.selectedFile = e.target.files[0] ?? null;
    }

    resetFile(): void {
        this.fileInput.nativeElement.value = '';
        this.selectedFile = null;
    }

    onTabChange(e: MatTabChangeEvent): void {
        if (e.index === FeedbackTab.History) {
            this.showSubmitBtn = false;
        } else {
            this.showSubmitBtn = true;
        }
    }

    resolved(captchaResponse: string) {
        this.recaptchaValue = captchaResponse;
        console.log(`Resolved captcha with response: ${captchaResponse}`);
    }
}
